import type {
  ClientMessage,
  Difficulty,
  GameMode,
  PlayerColor,
  PlayerSummary,
  ScoreEntry,
  ServerMessage,
} from '../../shared/protocol';
import { isLegacyServerMessage, normalizeServerMessage } from './protocolCompat';

export type NetworkEvent = ServerMessage['type'] | 'connected' | 'disconnected' | 'reconnecting' | 'reconnected';

export const resolveWebSocketUrl = (
  pageLocation: Pick<Location, 'protocol' | 'host'>,
  configured?: string,
): string => {
  if (configured?.trim()) return configured.trim();
  const protocol = pageLocation.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${pageLocation.host}/ws`;
};

export class NetworkClient extends EventTarget {
  socket?: WebSocket;
  id = '';
  roomCode = '';
  hostId = '';
  players: PlayerSummary[] = [];
  nickname = 'Paperotto';
  color: PlayerColor = 'cyan';
  legacyProtocol = false;
  private readonly sessionToken: string;
  private manualClose = false;
  private reconnectStartedAt = 0;
  private reconnectTimer?: number;
  private reconnectAckTimer?: number;

  constructor() {
    super();
    const stored = sessionStorage.getItem('qod-session-token');
    this.sessionToken = stored || crypto.randomUUID();
    sessionStorage.setItem('qod-session-token', this.sessionToken);
  }

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  get isHost(): boolean {
    return Boolean(this.id && this.id === this.hostId);
  }

  connect(): Promise<void> {
    if (this.connected && this.id) return Promise.resolve();
    this.manualClose = false;
    return this.openSocket(false);
  }

  send(message: ClientMessage): void {
    if (!this.connected) return;
    this.socket?.send(JSON.stringify(message));
  }

  createRoom(nickname: string, color: PlayerColor): void {
    this.nickname = nickname;
    this.color = color;
    this.send({ type: 'create-room', nickname, color, sessionToken: this.sessionToken });
  }

  joinRoom(nickname: string, roomCode: string, color: PlayerColor): void {
    this.nickname = nickname;
    this.color = color;
    this.send({
      type: 'join-room',
      nickname,
      color,
      roomCode: roomCode.trim().toUpperCase(),
      sessionToken: this.sessionToken,
    });
  }

  startGame(difficulty: Difficulty, mode: GameMode): void {
    this.send({ type: 'start-game', difficulty, mode });
  }

  submitScore(result: Omit<ScoreEntry, 'id' | 'nickname' | 'createdAt'>): void {
    this.send({ type: 'submit-score', result });
  }

  requestLeaderboard(): void {
    this.send({ type: 'request-leaderboard' });
  }

  leave(): void {
    this.manualClose = true;
    this.clearReconnectTimers();
    this.send({ type: 'leave-room' });
    this.socket?.close();
    this.roomCode = '';
    this.hostId = '';
    this.players = [];
  }

  on<T = unknown>(type: NetworkEvent, handler: (detail: T) => void): () => void {
    const listener: EventListener = (event) => handler((event as CustomEvent<T>).detail);
    this.addEventListener(type, listener);
    return () => this.removeEventListener(type, listener);
  }

  private openSocket(reconnecting: boolean): Promise<void> {
    const configured = import.meta.env.VITE_WS_URL as string | undefined;
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(resolveWebSocketUrl(location, configured));
      this.socket = socket;
      let settled = false;
      const fail = (message: string): void => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        reject(new Error(message));
      };
      const timeout = window.setTimeout(() => {
        fail('Il server cooperativo non risponde.');
        socket.close();
      }, 5000);

      socket.addEventListener('open', () => this.dispatch('connected', undefined), { once: true });
      socket.addEventListener('error', () => fail('Impossibile raggiungere la sala macchine.'), { once: true });
      socket.addEventListener('message', (event) => {
        try {
          const rawMessage = JSON.parse(String(event.data)) as ServerMessage;
          this.legacyProtocol ||= isLegacyServerMessage(rawMessage);
          const message = normalizeServerMessage(rawMessage);
          if (message.type === 'welcome') {
            this.id = message.id;
            if (!settled) {
              settled = true;
              window.clearTimeout(timeout);
              resolve();
              if (reconnecting) {
                this.send({
                  type: 'reconnect-room',
                  nickname: this.nickname,
                  color: this.color,
                  roomCode: this.roomCode,
                  sessionToken: this.sessionToken,
                });
                this.reconnectAckTimer = window.setTimeout(() => {
                  socket.close();
                }, 3200);
              }
            }
          }
          if (message.type === 'room-state') {
            this.roomCode = message.roomCode;
            this.hostId = message.hostId;
            this.players = message.players;
            if (reconnecting || this.reconnectStartedAt > 0) {
              this.clearReconnectTimers();
              this.reconnectStartedAt = 0;
              this.dispatch('reconnected', undefined);
            }
          }
          this.dispatch(message.type, message);
        } catch {
          this.dispatch('error', { type: 'error', message: 'Messaggio di rete illeggibile.' } satisfies ServerMessage);
        }
      });

      socket.addEventListener('close', () => {
        fail('Connessione alla sala macchine interrotta.');
        if (this.socket === socket) this.socket = undefined;
        if (!this.manualClose && this.roomCode) this.scheduleReconnect();
        else this.dispatch('disconnected', undefined);
      });
    });
  }

  private scheduleReconnect(): void {
    if (!this.reconnectStartedAt) {
      this.reconnectStartedAt = performance.now();
      this.dispatch('reconnecting', undefined);
    }
    this.clearReconnectTimers();
    if (performance.now() - this.reconnectStartedAt > 23_000) {
      this.reconnectStartedAt = 0;
      this.dispatch('disconnected', undefined);
      return;
    }
    const elapsed = performance.now() - this.reconnectStartedAt;
    const delay = elapsed < 4_000 ? 650 : elapsed < 12_000 ? 1_400 : 2_400;
    this.reconnectTimer = window.setTimeout(() => {
      void this.openSocket(true).catch(() => this.scheduleReconnect());
    }, delay);
  }

  private clearReconnectTimers(): void {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    if (this.reconnectAckTimer) window.clearTimeout(this.reconnectAckTimer);
    this.reconnectTimer = undefined;
    this.reconnectAckTimer = undefined;
  }

  private dispatch(type: NetworkEvent, detail: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }
}
